import { Badge } from '@radix-ui/themes';
import { CommonSelectInput } from 'components/common/form/select';
import { LegendDot } from 'components/sections/analytics/session-hits/recap/legend-dot';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import {
  MAP_OUTCOME_TO_PRETTY_LABEL,
  OutcomeCode,
} from 'lib_ts/enums/hitters.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';

const HIT_OUTCOME_OPTIONS: IOptionExt[] = [
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.Ball],
    value: OutcomeCode.Ball,
    group: 'No Contact',
    prefix: <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.Ball)} />,
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.Single],
    value: OutcomeCode.Single,
    group: 'Hits',
    prefix: <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.Single)} />,
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.Double],
    value: OutcomeCode.Double,
    group: 'Hits',
    prefix: <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.Double)} />,
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.Triple],
    value: OutcomeCode.Triple,
    group: 'Hits',
    prefix: <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.Triple)} />,
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.HomeRun],
    value: OutcomeCode.HomeRun,
    group: 'Hits',
    prefix: (
      <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.HomeRun)} />
    ),
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.GroundOut],
    value: OutcomeCode.GroundOut,
    group: 'Outs',
    prefix: (
      <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.GroundOut)} />
    ),
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.FlyOut],
    value: OutcomeCode.FlyOut,
    group: 'Outs',
    prefix: <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.FlyOut)} />,
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.FoulOut],
    value: OutcomeCode.FoulOut,
    group: 'Outs',
    prefix: (
      <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.FoulOut)} />
    ),
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.Foul],
    value: OutcomeCode.Foul,
    group: 'Other',
    prefix: <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.Foul)} />,
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.SwingingStrike],
    value: OutcomeCode.SwingingStrike,
    group: 'No Contact',
    prefix: (
      <LegendDot
        color={HitHelper.getOutcomeColor(OutcomeCode.SwingingStrike)}
      />
    ),
  },
  {
    label: MAP_OUTCOME_TO_PRETTY_LABEL[OutcomeCode.CalledStrike],
    value: OutcomeCode.CalledStrike,
    group: 'No Contact',
    prefix: (
      <LegendDot color={HitHelper.getOutcomeColor(OutcomeCode.CalledStrike)} />
    ),
  },
];

export const OutcomeOverrideSelector = (props: {
  hit: ISessionHit;
  szDef: IStrikeZone;
}) => {
  // for the case where there was no outcome, check if it was a ball / called strike / swinging strike
  const safeOutcome = HitHelper.getOutcome(props.hit, props.szDef);

  return (
    <CommonSelectInput
      id={`outcome-override-${props.hit._id}`}
      disabled={props.hit.tracking_device !== TrackingDevice.TrajektVision}
      options={HIT_OUTCOME_OPTIONS.map((m) =>
        m.value === props.hit.hit?.outcome
          ? {
              // show a badge for the detected outcome as a reminder
              ...m,
              menuSuffix: (
                <Badge size="1" color={RADIX.COLOR.NEUTRAL}>
                  Detected
                </Badge>
              ),
            }
          : m
      )}
      value={safeOutcome ?? OutcomeCode.Unknown}
      onChange={(v) =>
        useAnalyticsStore
          .getState()
          .updateHit(props.hit._id, { hit_outcome_override: v as OutcomeCode })
      }
      skipSort
    />
  );
};
